import React, { type MutableRefObject } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle, RotateCcw, ArrowRight, ExternalLink } from 'lucide-react';
import type { BundleRecommendation } from '../../types/prioritization-selector';
import { useToolSelectorStore } from '../../stores/prioritization/toolSelectorStore';

interface BundleResultsProps {
  recommendation: BundleRecommendation | null;
  onReset: () => void;
  /** Ref to the persisted tool_selector_events row ID, threaded from the parent page */
  eventIdRef?: MutableRefObject<string | null>;
}

const BundleResults: React.FC<BundleResultsProps> = ({
  recommendation,
  onReset,
  eventIdRef,
}) => {
  const navigate = useNavigate();
  const logToolOpened = useToolSelectorStore(s => s.logToolOpened);

  if (!recommendation) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-display font-bold text-[var(--text-pri)] mb-4">
          No recommendation available
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          There was an error generating your recommendation.
        </p>
        <button
          onClick={onReset}
          className="btn-primary"
        >
          Start Over
        </button>
      </div>
    );
  }

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)]';
      case 'medium':
        return 'text-[var(--mustard)] bg-[var(--mustard-dim)]';
      case 'low':
        return 'text-[var(--coral)] bg-[var(--coral-dim)]';
      default:
        return 'text-[var(--text-sec)] bg-[var(--hover-tint)]';
    }
  };

  // Map method IDs to integrated app tool paths
  const methodToToolPath: Record<string, string> = {
    'impact-effort-2x2': '/prioritization/tools/impact-effort-matrix',
    'eisenhower': '/prioritization/tools/eisenhower-matrix',
    'moscow': '/prioritization/tools/moscow-method',
    'rice': '/prioritization/tools/rice-scoring',
    'wsjf': '/prioritization/tools/wsjf',
    'stack-rank': '/prioritization/tools/stack-ranking',
    'pairwise': '/prioritization/tools/pairwise-ranking',
    'dot-voting': '/prioritization/tools/dot-voting',
    'buy-a-feature': '/prioritization/tools/buy-a-feature',
    'pareto': '/prioritization/tools/pareto-analysis',
    'cost-of-delay': '/prioritization/tools/cost-of-delay',
    'simple-weighted': '/prioritization/tools/simple-weighted-scoring',
  };

  const handleViewMethod = (methodId: string, methodName?: string) => {
    const toolPath = methodToToolPath[methodId];

    // Record the tool-open on the audit event (fire-and-forget)
    const eventId = eventIdRef?.current;
    if (eventId && toolPath) {
      logToolOpened(eventId, toolPath.replace('/prioritization/tools/', ''), methodName ?? methodId);
    }

    if (toolPath) {
      navigate(toolPath, { replace: false });
    } else {
      navigate(`method/${methodId}`);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--green-dim)] rounded-full mb-4">
          <CheckCircle className="w-8 h-8 text-[var(--green)]" />
        </div>
        <h1 className="text-3xl font-display font-bold text-[var(--text-pri)] mb-4">
          Perfect! Here's Your Recommendation
        </h1>
        <p className="text-lg text-[var(--text-sec)]">
          Based on your answers, here are the specific prioritization methods that will work best for your situation
        </p>
      </div>

      {/* Recommended Methods */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-display font-bold text-[var(--text-pri)] mb-2">
            Your Recommended Methods
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            Here are the specific prioritization methods that work best for your situation:
          </p>
          <div className="flex items-center justify-center space-x-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColor(recommendation.confidence)}`}>
              {recommendation.confidence} confidence
            </span>
            <span className="text-sm text-[var(--text-sec)]">
              {recommendation.recommendedMethods.length} methods recommended
            </span>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          {recommendation.recommendedMethods.map((method) => (
            <div key={method.id} className="p-6 bg-[var(--surface)] hover:bg-[var(--hover-tint)] transition-colors cursor-pointer" onClick={() => handleViewMethod(method.id, method.name)}>
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-semibold text-[var(--text-pri)] text-lg">{method.name}</h4>
                <ArrowRight className="w-5 h-5 text-[var(--text-ter)]" />
              </div>
              <p className="text-[var(--text-sec)] mb-3">{method.description}</p>
              <div className="flex items-center space-x-4 text-sm text-[var(--text-ter)]">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  method.complexity === 'simple' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                  method.complexity === 'moderate' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' :
                  'bg-[var(--coral-dim)] text-[var(--coral)]'
                }`}>
                  {method.complexity} complexity
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  method.timeRequired === 'quick' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' :
                  method.timeRequired === 'moderate' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' :
                  'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                }`}>
                  {method.timeRequired} time
                </span>
                <span className="text-[var(--text-ter)]">{method.teamSize} team</span>
              </div>
            </div>
          ))}
        </div>
      </div>


      {/* Reasoning */}
      <div className="bg-[var(--surface)] p-6 mb-8">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Why This Recommendation?</h3>
        <p className="text-[var(--text-sec)] mb-4">{recommendation.reasoning}</p>
        
        <div className="border-t border-[var(--rule)] pt-4">
          <h4 className="font-semibold text-[var(--text-pri)] mb-2">Confidence Level</h4>
          <p className="text-[var(--text-sec)]">{recommendation.confidenceExplanation}</p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
        <h3 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
          Next Steps
        </h3>
        <ol className="space-y-3">
          {recommendation.nextSteps.map((step, index) => (
            <li key={index} className="flex items-start">
              <span className="flex-shrink-0 w-6 h-6 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                {index + 1}
              </span>
              <span className="text-[var(--text-sec)]">{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={onReset}
          className="btn-primary flex items-center space-x-2"
        >
          <RotateCcw className="w-4 h-4" />
          <span>Start New Analysis</span>
        </button>
        <Link
          to="/prioritization/tools"
          className="btn-secondary flex items-center space-x-2"
        >
          <ExternalLink className="w-4 h-4" />
          <span>View All Tools</span>
        </Link>
        <button
          onClick={() => window.print()}
          className="btn-secondary flex items-center space-x-2"
        >
          <span>Print Results</span>
        </button>
      </div>
    </div>
  );
};

export default BundleResults;

